// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import NonTranslationTypography from "@sc/App/Components/NonTranslationTypography/NonTranslationTypography";
import { xGoogleAnalyticsState } from "@sc/App/GoogleAnalyticsConnector/Store/Reducers";
import { getDataLayer } from "@sc/App/GoogleAnalyticsConnector/Utilities/DataLayer";
import { safeJsonParse, xMask } from "@sc/XDL/Utilities";
import parse from "html-react-parser";
import * as React from "react";
import { connect } from "react-redux";

const enum fields {
    title = "title",
    description = "description",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.description]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => {
    const appState = state as xGoogleAnalyticsState;
    const session = getSession(state);
    const { salespersonInfo } = session.properties;

    return {
        salespersonInfo: safeJsonParse(salespersonInfo),
        xGoogleAnalytics: appState.xGoogleAnalytics,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface HaveQuestionsStyles {
    wrapperContainer?: InjectableCss;
    title?: TypographyPresentationProps;
    description?: TypographyPresentationProps;
    personalInfo?: {
        header?: TypographyPresentationProps;
        infoColumnContainer?: InjectableCss;
        name?: TypographyPresentationProps;
        accountLink?: LinkPresentationProps;
    };
}

export const haveQuestionsStyles: HaveQuestionsStyles = {};

const HaveQuestions: React.FC<Props> = ({ fields, salespersonInfo, xGoogleAnalytics }) => {
    const styles = haveQuestionsStyles;
    const { title, description } = fields;

    // XNG-Change: Track GA4 contact sales rep event
    const trackContactSalesEvent = (
        eventFlag: boolean | undefined,
        eventName: string | undefined,
        clickText: string,
    ) => {
        if (!eventFlag || !eventName) {
            return;
        }

        const dataLayer = getDataLayer();
        if (!dataLayer) {
            return;
        }

        const googleAnalyticsData: any = {
            event: eventName,
            click_text: clickText,
            affiliation: xGoogleAnalytics.affiliation || "",
            sales_rep: xGoogleAnalytics.salesRep || "",
            is_rep: xGoogleAnalytics.isRep || "",
            bill_to_num: xGoogleAnalytics.billToNum || "",
            ship_to_num: xGoogleAnalytics.shipToNum || "",
            cust_type: xGoogleAnalytics.customerType || "",
            login_type: xGoogleAnalytics.loginType || "",
            warehouse: xGoogleAnalytics.warehouse || "",
        };

        dataLayer.push({ ecommerce: null });
        dataLayer.push(googleAnalyticsData);
    };

    return (
        <StyledWrapper {...styles.wrapperContainer}>
            <Typography {...styles.title}>{title}</Typography>
            <Typography {...styles.description}>
                {typeof description === "string" ? parse(description, parserOptions) : description}
            </Typography>
            {salespersonInfo && (
                <StyledWrapper {...styles.personalInfo?.infoColumnContainer}>
                    <Typography {...styles.personalInfo?.header}>{translate("Sales Rep Info")}</Typography>
                    <NonTranslationTypography TypographyStyle={styles.personalInfo?.name}>
                        {salespersonInfo.name}
                    </NonTranslationTypography>
                    {salespersonInfo.phone1 && (
                        <Link
                            aria-label="Phone"
                            href={`tel:${salespersonInfo.phone1}`}
                            {...styles.personalInfo?.accountLink}
                            onClick={() => {
                                // XNG-Change: Track GA4 contact sales rep event
                                trackContactSalesEvent(
                                    xGoogleAnalytics?.contactSalesEvent,
                                    xGoogleAnalytics?.contactSalesEventName,
                                    salespersonInfo.phone1,
                                );
                            }}
                        >
                            {xMask.phoneNumberUS(salespersonInfo.phone1)}
                        </Link>
                    )}
                    {salespersonInfo.email && (
                        <Link
                            aria-label="email"
                            href={`mailto:${salespersonInfo.email}`}
                            {...styles.personalInfo?.accountLink}
                            onClick={() => {
                                // XNG-Change: Track GA4 contact sales rep event
                                trackContactSalesEvent(
                                    xGoogleAnalytics?.contactSalesEvent,
                                    xGoogleAnalytics?.contactSalesEventName,
                                    salespersonInfo.email,
                                );
                            }}
                        >
                            {salespersonInfo.email}
                        </Link>
                    )}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(HaveQuestions),
    definition: {
        group: "Cart",
        allowedContexts: ["CartPage", "OnePageCheckoutPage", "OnePageCheckoutConfirmationPage"],
        displayName: "Have Questions",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.description,
                displayName: "description",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
